import client from './client';

export type CertificateType = 'Baptism' | 'Confirmation' | 'Marriage' | 'Burial';

export interface CertificateListItem {
  certificateId:   number;
  certificateType: CertificateType;
  refSerialNo:     string | null;
  issuedDate:      string | null;
  createdAt:       string;
  familyId:        number | null;
  primaryName:     string | null;
}

export interface CertificateMaster {
  certificateId:   number;
  certificateType: CertificateType;
  refSerialNo:     string | null;
  keptAt:          string | null;
  issuedDate:      string | null;
  createdBy:       number | null;
  createdAt:       string;
  familyId:        number | null;
  familyMemberId:  number | null;
}

// ── Type-specific detail shapes ───────────────────────────────────────────────

export interface BaptismDetail {
  dateOfBaptism?:     string;
  dateOfBirth?:       string;
  placeOfBirth?:      string;
  name?:              string;
  sex?:               string;
  fatherName?:        string;
  motherName?:        string;
  residence?:         string;
  fatherProfession?:  string;
  godParents?:        string;
  ministerOfBaptism?: string;
  confirmed?:         string;
  married?:           string;
  remarks?:           string;
}

export interface ConfirmationDetail {
  nameAndSurname?: string;
  age?:            number | string;
  whenConfirmed?:  string;
  whereConfirmed?: string;
  parentsNames?:   string;
  sponsorName?:    string;
  bishopsName?:    string;
  remarks?:        string;
}

export interface MarriageDetail {
  dateOfMarriage?:      string;
  placeOfMarriage?:     string;
  bridegroomName?:      string;
  bridegroomAge?:       number | string;
  bridegroomCondition?: string;
  bridegroomProfession?:string;
  bridegroomResidence?: string;
  bridegroomParents?:   string;
  brideName?:           string;
  brideAge?:            number | string;
  brideCondition?:      string;
  brideProfession?:     string;
  brideResidence?:      string;
  brideParents?:        string;
  byBannsOrLicence?:   string;
  witness?:             string;
  minister?:            string;
  groomFamilyId?:       number;
  groomFamilyMemberId?: number;
  brideFamilyId?:       number;
  brideFamilyMemberId?: number;
}

export interface BurialDetail {
  nameAndSurname?:             string;
  sex?:                        string;
  age?:                        number | string;
  whenDied?:                   string;
  parentsOrSpouse?:            string;
  tradeProfession?:            string;
  causeOfDeath?:               string;
  whenBuried?:                 string;
  whereBuried?:                string;
  lastSacraments?:             string;
  confesViaticAnointing?:      string;
  ministerOfLastSacraments?:   string;
  buriedBy?:                   string;
  remarks?:                    string;
  nonParishCemeteryId?:        number;
}

export type CertificateDetail = BaptismDetail | ConfirmationDetail | MarriageDetail | BurialDetail;

export interface CertificateFull extends CertificateMaster {
  detail: CertificateDetail;
}

export interface CertificatePayload {
  type:            CertificateType;
  refSerialNo?:    string;
  keptAt?:         string;
  issuedDate?:     string;
  familyId?:       number;
  familyMemberId?: number;
  detail:          CertificateDetail;
}

// ── API calls ─────────────────────────────────────────────────────────────────

export const listCertificates = async (params?: {
  type?: CertificateType | '';
  year?: number;
  familyId?: number;
}): Promise<CertificateListItem[]> => {
  const p: Record<string, string> = {};
  if (params?.type)     p.type     = params.type;
  if (params?.year)     p.year     = String(params.year);
  if (params?.familyId) p.familyId = String(params.familyId);
  const { data } = await client.get<{ data: CertificateListItem[] }>('/certificates', { params: p });
  return data.data;
};

export const getCertificate = async (id: number): Promise<CertificateFull> => {
  const { data } = await client.get<{ data: CertificateFull }>(`/certificates/${id}`);
  return data.data;
};

export const createCertificate = async (payload: CertificatePayload): Promise<{ certificateId: number }> => {
  const { data } = await client.post<{ data: { certificateId: number } }>('/certificates', payload);
  return data.data;
};

export const updateCertificate = async (id: number, payload: Partial<CertificatePayload>): Promise<void> => {
  await client.put(`/certificates/${id}`, payload);
};

export const deleteCertificate = async (id: number): Promise<void> => {
  await client.delete(`/certificates/${id}`);
};
